import { getTokenSilently } from "@/utils/auth";
import { acceptLanguageHeader } from "@/lib/i18n/headers";
import {
  ApolloClient,
  createHttpLink,
  InMemoryCache,
  ApolloLink,
  ServerError,
  Observable,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import { RetryLink } from "@apollo/client/link/retry";
import { getMainDefinition } from "@apollo/client/utilities";
import * as Sentry from "@sentry/react";
import { getAppType } from "../hooks/use-app-type";
import { CoffeeAppType } from "../types/app";
import {
  WORK_EMAIL_REQUIRED_PATH,
  getFreeEmailSignupBlock,
  workEmailRequiredPath,
} from "@/lib/auth/free-email-block";

const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) => {
        // Only capture non-auth GraphQL errors
        if (
          !message.includes("UnauthenticatedUser") &&
          !message.includes("UNAUTHENTICATED")
        ) {
          Sentry.withScope((scope) => {
            scope.setTag("error_type", "graphql_error");
            scope.setContext("graphql", {
              operation: operation.operationName,
              path,
              locations,
            });
            Sentry.captureException(
              new Error(
                `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(
                  locations
                )}, Path: ${path}`
              )
            );
          });
        }
      });
    }
    // A free-email signup refused by the backend gate. Terminal, so route to the
    // explainer screen rather than falling through to the 401 refresh-and-retry path
    // (and don't report it to Sentry — it's an expected product refusal, not a fault).
    // Deliberately not a logout() call: per the auth rules, AuthProvider owns that.
    const freeEmailBlock = getFreeEmailSignupBlock(networkError);
    if (freeEmailBlock) {
      if (
        typeof window !== "undefined" &&
        !window.location.pathname.startsWith(WORK_EMAIL_REQUIRED_PATH)
      ) {
        window.location.assign(workEmailRequiredPath(freeEmailBlock.email));
      }
      return;
    }

    if (networkError) {
      const statusCode = (networkError as ServerError)?.statusCode;
      // Don't capture 401s (handled by auth refresh) or aborted requests
      const isAborted =
        networkError.message?.includes("AbortError") ||
        networkError.name === "AbortError" ||
        (networkError.message?.includes("Failed to fetch") &&
          typeof document !== "undefined" &&
          document.visibilityState === "hidden");
      if (statusCode !== 401 && !isAborted) {
        Sentry.withScope((scope) => {
          scope.setTag("error_type", "network_error");
          scope.setTag("status_code", statusCode || "unknown");
          scope.setContext("network", {
            operation: operation.operationName,
            statusCode,
            message: networkError.message,
          });
          if (networkError.message?.includes("Failed to fetch")) {
            scope.setLevel("warning");
          }
          Sentry.captureException(
            new Error(`[Network error]: ${networkError}`)
          );
        });
      }
    }

    // Detect auth errors — narrowed to specific codes only
    let authError = false;

    if (
      graphQLErrors?.find(
        (_) =>
          _.extensions?.code === "UNAUTHENTICATED" ||
          _.message.includes("UnauthenticatedUser") ||
          _.message.includes("Authentication credentials were not provided") ||
          _.message.includes("not authenticated")
      )
    ) {
      authError = true;
    }

    if ((networkError as ServerError)?.statusCode === 401) {
      authError = true;
    }

    if (authError) {
      console.log('Apollo: Auth error detected, attempting token refresh...');
      // Force a fresh token from the SDK (bypasses cache)
      return new Observable((observer) => {
        getTokenSilently({ cacheMode: 'off' } as any)
          .then((newToken) => {
            if (!newToken) {
              console.error('Apollo: Token refresh failed - no token returned');
              throw new Error('Token refresh failed');
            }
            console.log('Apollo: Token refreshed successfully, retrying request');
            operation.setContext(({ headers = {} }: { headers: Record<string, string> }) => ({
              headers: {
                ...headers,
                authorization: `Bearer ${newToken}`,
              },
            }));
            return forward(operation);
          })
          .then((observable) => {
            observable.subscribe(observer);
          })
          .catch((error) => {
            console.error('Apollo: Token refresh error:', error);
            observer.error(error);
          });
      });
    }
  }
);

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: 3000,
    jitter: true,
  },
  attempts: {
    max: 3,
    retryIf: (error, operation) => {
      // Never replay a mutation. "No status code" also covers a response the server did
      // get, e.g. the ALB's CORS-less 504 when a request outlives its idle timeout, so a
      // retry re-runs the write (testAgentDefinition: a fresh multi-minute agent run each time).
      const definition = getMainDefinition(operation.query);
      if (definition.kind === 'OperationDefinition' && definition.operation === 'mutation') {
        return false;
      }
      // Never resurrect an intentionally-cancelled request (e.g. windowed grids
      // aborting superseded page fetches via AbortController) — an abort has no
      // status code and would otherwise look like a retryable network error.
      if (error?.name === 'AbortError') return false;
      // Retry on network errors (no status code = request never reached server)
      return !!error && !error.statusCode;
    },
  },
});

const httpLink = createHttpLink({
  uri: import.meta.env.VITE_COFFEE_GRAPHQL_URL || "https://coffee.dev.coffee.work/graphql/",
  fetch: (uri, options) => {
    if (!!options) {
      options.redirect = "follow";
    }
    return fetch(uri, options);
  },
});

const authLink = setContext(async (_, { headers }) => {
  // Get a valid token from the Auth0 SDK (handles refresh automatically)
  const token = await getTokenSilently();
  const appType: CoffeeAppType = getAppType();

  const authHeaders: Record<string, string> = {
    ...headers,
    "X-App-Type": appType,
    // Resolved per request, not at module load — the user can switch language
    // mid-session. The backend outranks this with a saved user preference, so
    // it decides only for users who have never picked one.
    ...acceptLanguageHeader(),
  };

  if (token) {
    authHeaders.authorization = `Bearer ${token}`;
  }

  return {
    headers: authHeaders,
  };
});

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([errorLink, retryLink, authLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      // GridSummary has no id field — without merge: true, Apollo throws when
      // two queries with different filters both return GridSummary results,
      // corrupting internal state and preventing other queries (like getDeals)
      // from delivering data.
      GridSummary: { merge: true },
      DealGridSummary: { merge: true },
      FillRateDatum: { merge: true },
      LineItemProperties: { merge: true },
      ProductProperties: { merge: true },
    },
  }),
  // Surface partial data when the server returns both `data` and `errors` —
  // the default "none" discards `data`, blanking entity grids. Mutations
  // intentionally keep the default so they still throw on failure.
  defaultOptions: {
    watchQuery: { errorPolicy: 'all' },
    query: { errorPolicy: 'all' },
  },
});
